import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { shareReplay } from 'rxjs/operators'
import { getObject, putObject } from './local-storage/local-storage-utils'
import { isUiScale, resolveScale, UiScale } from './ui-scale'

const AccountListBellConfigurationKey = 'account-list-bell-config'

/**
 * The scale currently written to the DOM. ModalPositionService divides
 * coordinates by this, so it must not re-resolve instead: re-resolving an `Auto`
 * preference reads the current viewport, while the DOM still holds what was
 * applied at bootstrap.
 *
 * Module-level rather than instance state, which is only safe because the service
 * is `providedIn: 'root'`.
 */
let appliedScale = 1

/**
 * The only place the UI scale is written to the DOM.
 *
 * `zoom` goes on `body`, not the Angular app-root element: ng-bootstrap appends
 * modals to `body` as siblings of app-root, so zooming app-root would leave them
 * full-size against a scaled shell. `--hs-ui-scale` goes on `documentElement`,
 * where `styles.scss` declares its fallback.
 */
function applyScaleToDocument(doc: Document, scale: number) {
  // Everything downstream compensates for a zoom it assumes took effect — the
  // `calc(Nvh / var(--hs-ui-scale, 1))` sites, appliedUiScale(), scaleFactor. On
  // an engine without `zoom` the property would still read 0.8 and the shell
  // would compute 125% of the viewport, so fall back to 1, where every one of
  // those sites is an identity. `.browserslistrc` still lists Firefox ESR, and
  // `zoom` only became Baseline in Firefox 126.
  // jsdom has no CSS global; nothing renders there, so treat "cannot ask" as
  // supported rather than silently disabling the feature under test.
  const canAsk =
    typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
  const applied = !canAsk || CSS.supports('zoom', `${scale}`) ? scale : 1

  appliedScale = applied
  doc.body.style.setProperty('zoom', `${applied}`)
  doc.documentElement.style.setProperty('--hs-ui-scale', `${applied}`)
}

export enum DensityLevel {
  Compact = 'compact',
  Comfortable = 'comfortable',
}

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  _densityLevelSubject$ = new BehaviorSubject<DensityLevel>(
    UserPreferencesService.getDensityLevel(),
  )

  _uiScaleSubject$ = new BehaviorSubject<UiScale>(
    UserPreferencesService.getUiScale(),
  )

  _focusModeSubject$ = new BehaviorSubject<boolean>(
    UserPreferencesService.getFocusMode(),
  )

  _hideDeletePromptSubject$ = new BehaviorSubject<boolean>(
    UserPreferencesService.getHideDeletePromptPreference(),
  )
  _autoRefreshAccountSubject$ = new BehaviorSubject<boolean>(
    UserPreferencesService.getAutoRefreshAccount(),
  )

  _bulkDialogSortPromptSubject$ = new BehaviorSubject<boolean>(
    UserPreferencesService.getBulkDialogSortPreference(),
  )

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  static getDensityLevel(): DensityLevel {
    // The body class has to be applied on the default path too, not just when a
    // preference is stored: grids read the level off the observable, so without
    // it a first-time user gets compact row heights but comfortable chrome.
    const level =
      (localStorage.getItem('helios-density-level') as DensityLevel) ||
      DensityLevel.Compact
    document.body.classList.add('helios-' + level)
    return level
  }

  // `doc` is injectable only so tests can assert the write: jsdom drops `zoom` as
  // a non-standard property, so getPropertyValue cannot tell "written" from
  // "never called".
  static getUiScale(doc: Document = document): UiScale {
    const stored = localStorage.getItem('helios-ui-scale')
    const pref = isUiScale(stored) ? stored : UiScale.Auto
    applyScaleToDocument(doc, resolveScale(pref))
    return pref
  }

  static getFocusMode() {
    return localStorage.getItem('helios-focus-mode') === '1'
  }

  static getReduxDevtools() {
    return localStorage.getItem('helios-redux-devtools') === '1'
  }

  static getHideDeletePromptPreference() {
    return localStorage.getItem('helios-hide-delete-prompt') === '1'
  }

  static getForceCloseSessionPreference() {
    return localStorage.getItem('helios-force-close-session') === '1'
  }

  static getAutoRefreshAccount() {
    const autoRefresh = localStorage.getItem('helios-auto-refresh-account')
    return autoRefresh ? autoRefresh === '1' : true
  }

  static getBulkDialogSortPreference() {
    return localStorage.getItem('helios-bulk-dialog-sort-prompt') === '1'
  }

  static getUseOldGridMultiRow(): boolean {
    return localStorage.getItem('helios-use-old-multi-row') === '1'
  }

  static getSaveModalPositions(): boolean {
    const value = localStorage.getItem('helios-save-modal-positions')

    // If the preference doesn't exist, set it to true (default) and return true
    if (value === null) {
      localStorage.setItem('helios-save-modal-positions', '1')
      return true
    }

    // Otherwise, return the stored value
    return value === '1'
  }

  areGridLinesRemoved() {
    if (localStorage.getItem('helios-remove-grid-lines') === '1') {
      this.document.body.classList.add('remove-helios-grid-lines')
      return true
    }

    return false
  }

  getDensityLevel(): Observable<DensityLevel> {
    return this._densityLevelSubject$.asObservable().pipe(shareReplay(1))
  }

  /**
   * A plain accessor rather than an observable: the only consumer is the
   * preferences drawer seeding a form control, and subscribing there would loop
   * (control → setUiScale → next → setValue → valueChanges → setUiScale).
   */
  get uiScale(): UiScale {
    return this._uiScaleSubject$.value
  }

  /** See `appliedScale`. */
  get scaleFactor(): number {
    return appliedScale
  }

  getFocusMode(): Observable<boolean> {
    return this._focusModeSubject$.asObservable()
  }

  getDeletePromptPreference(): Observable<boolean> {
    return this._hideDeletePromptSubject$.asObservable()
  }

  getAutoRefreshAccount(): Observable<boolean> {
    return this._autoRefreshAccountSubject$.asObservable()
  }

  getBulkDialogSortPromptPreference(): Observable<boolean> {
    return this._bulkDialogSortPromptSubject$.asObservable()
  }

  setDensityLevel(level: DensityLevel) {
    this.document.body.classList.remove(
      'helios-' + DensityLevel.Compact,
      'helios-' + DensityLevel.Comfortable,
    )
    this.document.body.classList.add('helios-' + level)
    localStorage.setItem('helios-density-level', level)
    this._densityLevelSubject$.next(level)
  }

  setUiScale(pref: UiScale) {
    applyScaleToDocument(this.document, resolveScale(pref))
    localStorage.setItem('helios-ui-scale', pref)
    this._uiScaleSubject$.next(pref)
  }

  setFocusMode(enabled: boolean, ignoreFullScreenActions: boolean = false) {
    this._focusModeSubject$.next(enabled)

    if (ignoreFullScreenActions) {
      return
    }

    if (enabled) {
      this.document.documentElement.requestFullscreen()
    } else {
      this.document.exitFullscreen()
    }
  }

  setReduxDevtools(enabled: boolean) {
    localStorage.setItem('helios-redux-devtools', enabled ? '1' : '0')
  }

  setUseOldGridMultiRow(enabled: boolean) {
    localStorage.setItem('helios-use-old-multi-row', enabled ? '1' : '0')
  }

  setSaveModalPositions(enabled: boolean) {
    localStorage.setItem('helios-save-modal-positions', enabled ? '1' : '0')
  }
  setDeletePromptPreference(hidePrompt: boolean) {
    localStorage.setItem('helios-hide-delete-prompt', hidePrompt ? '1' : '0')
    this._hideDeletePromptSubject$.next(hidePrompt)
  }

  setForceCloseSessionPreference(forceCloseSession: boolean) {
    localStorage.setItem(
      'helios-force-close-session',
      forceCloseSession ? '1' : '0',
    )
  }

  setAutoRefreshAccount(autoRefresh: boolean) {
    localStorage.setItem('helios-auto-refresh-account', autoRefresh ? '1' : '0')
    this._autoRefreshAccountSubject$.next(autoRefresh)
  }

  setBulkDialogSortPreference(hidePrompt: boolean) {
    localStorage.setItem(
      'helios-bulk-dialog-sort-prompt',
      hidePrompt ? '1' : '0',
    )
    this._bulkDialogSortPromptSubject$.next(hidePrompt)
  }

  removeGridLines(removed: boolean) {
    localStorage.setItem('helios-remove-grid-lines', removed ? '1' : '0')
    if (removed) {
      this.document.body.classList.add('remove-helios-grid-lines')
    } else {
      this.document.body.classList.remove('remove-helios-grid-lines')
    }
  }

  getAccountListBellConfiguration() {
    return getObject<{
      userNames: string[]
      accountOwnerField: 'opsManager' | 'reconciler' | 'manager'
    }>(AccountListBellConfigurationKey)
  }

  setAccountListBellConfiguration(
    userNames: string[],
    accountOwnerField: 'opsManager' | 'reconciler' | 'manager',
  ) {
    return putObject<{
      userNames: string[]
      accountOwnerField: 'opsManager' | 'reconciler' | 'manager'
    }>(AccountListBellConfigurationKey, {
      userNames,
      accountOwnerField,
    })
  }
}
